import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import profilePic from '../assets/harish.png';
import awsLogo from '../assets/aws.png';

const About = () => {
  // State management
  const [counters, setCounters] = useState({ ownership: 0, projects: 0, commits: 0, features: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const [activeSkill, setActiveSkill] = useState(null);
  const [terminalLines, setTerminalLines] = useState([]);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  const [activeTab, setActiveTab] = useState('frontend');
  const [hoveredStat, setHoveredStat] = useState(null);
  
  const sectionRef = useRef(null);
  const profileRef = useRef(null);

  // Terminal content with realistic commands
  const terminalContent = useMemo(() => [
    { type: 'command', text: '$ whoami' },
    { type: 'output', text: 'harish_chittoor [Frontend Engineer]' },
    { type: 'command', text: '$ cat current_stack.json' },
    { type: 'output', text: '{ "primary": "React 18 + TypeScript",' },
    { type: 'output', text: '  "state": "Redux Toolkit | Zustand",' },
    { type: 'output', text: '  "styling": "TailwindCSS | CSS-in-JS" }' },
    { type: 'command', text: '$ npm run performance-audit' },
    { type: 'output', text: '✓ Lighthouse Score: 98/100' },
    { type: 'output', text: '✓ First Contentful Paint: 0.8s' },
    { type: 'output', text: '✓ Time to Interactive: 1.2s' },
    { type: 'command', text: '$ git shortlog -sn | head -1' },
    { type: 'output', text: '  2847  Harish Chittoor' },
    { type: 'command', text: '$ echo "Status: $AVAILABILITY"' },
    { type: 'output', text: '🟢 Open to new opportunities!' },
  ], []);

  // Skills data with proficiency levels
  const skillsData = useMemo(() => ({
    frontend: [
      { name: 'React/Next.js', level: 95, icon: '⚛️', years: 5 },
      { name: 'TypeScript', level: 92, icon: '📘', years: 4 },
      { name: 'Vue.js', level: 85, icon: '💚', years: 3 },
      { name: 'CSS/Tailwind', level: 95, icon: '🎨', years: 5 },
      { name: 'JavaScript ES6+', level: 98, icon: '⚡', years: 5 },
    ],
    backend: [
      { name: 'Node.js', level: 88, icon: '🟢', years: 4 },
      { name: 'Python/Django', level: 80, icon: '🐍', years: 3 },
      { name: 'GraphQL', level: 85, icon: '◈', years: 3 },
      { name: 'REST APIs', level: 92, icon: '🔗', years: 5 },
    ],
    tools: [
      { name: 'Git/GitHub', level: 95, icon: '📦', years: 5 },
      { name: 'Docker', level: 82, icon: '🐳', years: 3 },
      { name: 'AWS', level: 78, icon: '☁️', years: 3 },
      { name: 'CI/CD', level: 85, icon: '🔄', years: 4 },
    ]
  }), []);

  // Stats data
  const stats = useMemo(() => [
    { key: 'ownership', label: 'End-to-End Products Owned', value: 4, suffix: '+', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/trello/trello-plain.svg', description: 'Designed, built & shipped to production', isImage: true },
    { key: 'projects', label: 'Projects Shipped', value: 15, suffix: '+', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/circleci/circleci-plain.svg', description: 'Across various domains', isImage: true },
    { key: 'commits', label: 'Git Commits', value: 2847, suffix: '', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg', description: 'Lines of quality code', isImage: true },
    { key: 'features', label: 'Features Delivered', value: 40, suffix: '+', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jira/jira-original.svg', description: 'User-facing shipped to production', isImage: true },
  ], []);

  // Quick facts about me
  const quickFacts = useMemo(() => [
    { icon: '✅', label: 'Work Authorization', value: 'OPT Eligible', detail: 'Available immediately · No sponsorship needed' },
    { icon: '💼', label: 'Notice Period', value: 'Available Now', detail: 'Can start immediately' },
    { icon: '🎯', label: 'Performance Track Record', value: '98+ Lighthouse Score', detail: 'Consistently optimized apps' },
    { icon: '🌐', label: 'Languages', value: 'English, Hindi, Telugu', detail: 'Fluent in all' },
    { icon: '⏰', label: 'Timezone', value: 'CST (UTC-6)', detail: 'Flexible hours' },
    { icon: '🚀', label: 'Full-Stack Capable', value: 'Frontend + Backend', detail: 'React, Node.js, APIs' },
  ], []);

  // Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animate counters
  useEffect(() => {
    if (!isVisible) return;
    const duration = 2500;
    const steps = 80;
    let currentStep = 0;
    
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easeOut = 1 - Math.pow(1 - progress, 4);
      
      setCounters({
        ownership: Math.floor(4 * easeOut),
        projects: Math.floor(15 * easeOut),
        commits: Math.floor(2847 * easeOut),
        features: Math.floor(40 * easeOut),
      });
      
      if (currentStep >= steps) clearInterval(interval);
    }, duration / steps);
    
    return () => clearInterval(interval);
  }, [isVisible]);

  // Terminal typing effect
  useEffect(() => {
    if (!isVisible || currentLineIndex >= terminalContent.length) return;
    const delay = terminalContent[currentLineIndex]?.type === 'command' ? 1000 : 200;
    const timer = setTimeout(() => {
      setTerminalLines(prev => [...prev, terminalContent[currentLineIndex]]);
      setCurrentLineIndex(prev => prev + 1);
    }, delay);
    return () => clearTimeout(timer);
  }, [isVisible, currentLineIndex, terminalContent]);

  // 3D profile tilt effect
  const handleMouseMove = useCallback((e) => {
    if (!profileRef.current) return;
    const rect = profileRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setMousePosition({ x: x * 25, y: y * -25 });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setMousePosition({ x: 0, y: 0 });
  }, []);

  const currentSkills = skillsData[activeTab] || skillsData.frontend;

  return (
    <section id="about" className="about-ultimate" ref={sectionRef} style={{ position: 'relative' }}>
      {/* Tech Stack Banner - Transparent between navbar and hero */}
      <div className="tech-stack-banner-hero">
        <div className="tech-stack-scroll-container">
          <div className="tech-stack-scroll-content">
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" alt="React" className="tech-logo" />
              <span>React</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg" alt="TypeScript" className="tech-logo" />
              <span>TypeScript</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redux/redux-original.svg" alt="Redux" className="tech-logo" />
              <span>Redux</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg" alt="Next.js" className="tech-logo" />
              <span>Next.js</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg" alt="Node.js" className="tech-logo" />
              <span>Node.js</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-original.svg" alt="TailwindCSS" className="tech-logo" />
              <span>TailwindCSS</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src={awsLogo} alt="AWS" className="tech-logo" />
              <span>AWS</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/githubactions/githubactions-original.svg" alt="CI/CD" className="tech-logo" />
              <span>CI/CD</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg" alt="UI/UX" className="tech-logo" />
              <span>UI/UX</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redux/redux-original.svg" alt="State Mgmt" className="tech-logo" />
              <span>State Mgmt</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg" alt="APIs & GraphQL" className="tech-logo" />
              <span>APIs & GraphQL</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tensorflow/tensorflow-original.svg" alt="AI" className="tech-logo" />
              <span>AI</span>
            </span>
          </div>
          <div className="tech-stack-scroll-content" aria-hidden="true">
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" alt="React" className="tech-logo" />
              <span>React</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg" alt="TypeScript" className="tech-logo" />
              <span>TypeScript</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redux/redux-original.svg" alt="Redux" className="tech-logo" />
              <span>Redux</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg" alt="Next.js" className="tech-logo" />
              <span>Next.js</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg" alt="Node.js" className="tech-logo" />
              <span>Node.js</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-original.svg" alt="TailwindCSS" className="tech-logo" />
              <span>TailwindCSS</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src={awsLogo} alt="AWS" className="tech-logo" />
              <span>AWS</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/githubactions/githubactions-original.svg" alt="CI/CD" className="tech-logo" />
              <span>CI/CD</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg" alt="UI/UX" className="tech-logo" />
              <span>UI/UX</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redux/redux-original.svg" alt="State Mgmt" className="tech-logo" />
              <span>State Mgmt</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg" alt="APIs & GraphQL" className="tech-logo" />
              <span>APIs & GraphQL</span>
            </span>
            <span>·</span>
            <span className="tech-banner-item">
              <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tensorflow/tensorflow-original.svg" alt="AI" className="tech-logo" />
              <span>AI</span>
            </span>
          </div>
        </div>
      </div>

      {/* Animated Grid Background */}
      <div className="about-grid-bg">
        <div className="grid-lines"></div>
      </div>
      
      {/* Floating Particles */}
      <div className="about-particles-container">
        {[...Array(30)].map((_, i) => (
          <div key={i} className="floating-particle" style={{
            '--delay': `${i * 0.3}s`,
            '--x-start': `${Math.random() * 100}%`,
            '--duration': `${20 + Math.random() * 15}s`,
            '--size': `${3 + Math.random() * 4}px`
          }} />
                  ))}
                </div>

      <div className="about-ultimate-container">

        {/* Hero Profile Section */}
        <div className="about-hero-section" style={{ position: 'relative', zIndex: 1, marginTop: '20px' }}>
          {/* Left: 3D Profile Card */}
          <div 
            className="profile-3d-container"
            ref={profileRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <div 
              className="profile-3d-card"
              style={{
                transform: `perspective(1500px) rotateY(${mousePosition.x}deg) rotateX(${mousePosition.y}deg) translateY(${scrollY * 0.02}px)`
              }}
            >
              {/* Holographic Border */}
              <div className="holographic-border"></div>
              
              {/* Profile Image */}
              <div className="profile-image-container">
                <div className="profile-ring profile-ring-1"></div>
                <div className="profile-ring profile-ring-2"></div>
                <div className="profile-ring profile-ring-3"></div>
                <img src={profilePic} alt="Headshot of Harish Chittoor" className="profile-main-image" />
                <div className="profile-glow-effect"></div>
              </div>

              {/* Status Badge */}
              <div className="profile-status-badge">
                <span className="status-indicator"></span>
                <span>Available for Hire</span>
                    </div>
                    
              {/* Name & Title */}
              <div className="profile-identity">
                <h1 className="profile-name">Harish Chittoor</h1>
                <div className="profile-title-wrapper">
                  <span className="profile-title">Frontend Engineer</span>
                  <div className="title-underline"></div>
                </div>
              </div>

              {/* Quick Info Pills */}
              <div className="profile-pills">
                <span className="info-pill location-pill">
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" alt="React" className="pill-icon pill-logo" />
                  React Expert · Building fast, scalable, production-ready apps
                </span>
                <span className="info-pill location-pill">
                  <span className="pill-icon">📍</span>
                  Irving, TX
                </span>
                <span className="info-pill experience-pill">
                  <span className="pill-icon">💼</span>
                  4+ Years Experience
                </span>
                </div>

              {/* Social Links */}
              <div className="profile-social-row">
                <a href="https://github.com/ChittoorHarish" target="_blank" rel="noopener noreferrer" className="social-btn github">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/></svg>
                  <span>GitHub</span>
                </a>
                <a href="https://www.linkedin.com/in/harish-chittoor/" target="_blank" rel="noopener noreferrer" className="social-btn linkedin">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  <span>LinkedIn</span>
                </a>
                <a href="mailto:chittoorharish989@gmail.com" className="social-btn email">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
                  <span>Email</span>
                </a>
              </div>

              {/* Download Resume Button */}
              <a href="/resume.pdf" download className="download-resume-btn" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span className="btn-icon">📄</span>
                <span>Download Resume</span>
                <div className="btn-shine"></div>
              </a>
              
              {/* Schedule a Call Button */}
              {/* <a href="https://calendly.com/your-link" target="_blank" rel="noopener noreferrer" className="download-resume-btn" style={{ marginTop: '12px', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span className="btn-icon">📅</span>
                <span>Schedule a Call (15 min)</span>
                <div className="btn-shine"></div>
              </a> */}
            </div>
          </div>

          {/* Right: Bio & Terminal */}
          <div className="about-content-section" style={{ position: 'relative' }}>
            {/* Section Badge */}
            <div className="content-badge" style={{ position: 'relative', zIndex: 1 }}>
              <div className="badge-glow"></div>
              <span className="badge-icon">👨‍💻</span>
              <span>About Me</span>
        </div>

            {/* Main Headline */}
            <h2 className="about-headline" style={{ position: 'relative', zIndex: 1 }}>
              Building <span className="gradient-text">Exceptional</span> Digital Experiences
            </h2>

            {/* Bio Text */}
            <div className="about-bio-text" style={{ position: 'relative', zIndex: 1 }}>
              <p>
                I'm a <strong>Frontend Engineer</strong> with 4 years building 
                high-performance web and mobile apps and a recent Master's graduate. Fresh off completing 
                my <strong>Master's in IT & Management from UT Dallas (May 2025)</strong>, I am 
                immediately available and <span className="company-highlight">OPT Eligible</span>. 
                From enterprise solutions at <span className="company-highlight"> IBM</span> to 
                innovative startups, I deliver technical excellence and creative problem-solving.
              </p>
              <p>
                Expert in <strong>React + TypeScript</strong> and modern frontend architecture. 
                Achieving <span className="metric-highlight"> 98+ Lighthouse scores</span>. 
                Great software = clean code + thoughtful UX + attention to detail.
              </p>
            </div>

            {/* Live Terminal */}
            <div className="terminal-container" style={{ position: 'relative', zIndex: 1 }}>
              <div className="terminal-window">
                <div className="terminal-header">
                  <div className="terminal-controls">
                    <span className="control red"></span>
                    <span className="control yellow"></span>
                    <span className="control green"></span>
                  </div>
                  <span className="terminal-title">harish@portfolio:~</span>
                  <div className="terminal-actions">
                    <span className="action-dot">⋯</span>
                  </div>
                </div>
                <div className="terminal-content">
                {terminalLines.map((line, idx) => (
                    <div key={idx} className={`term-line ${line.type}`}>
                      {line.type === 'command' ? (
                        <><span className="prompt">❯</span> {line.text}</>
                      ) : (
                        <span className="output">{line.text}</span>
                      )}
                  </div>
                ))}
                  {currentLineIndex < terminalContent.length && (
                    <div className="cursor-line">
                      <span className="prompt">❯</span>
                      <span className="cursor">▋</span>
                    </div>
                )}
              </div>
            </div>
              </div>
            </div>
          </div>

        {/* Stats Section */}
        <div className="stats-showcase">
          <div className="stats-header">
            <h3 className="stats-title">
              <span className="title-icon">📊</span>
              Career Highlights
            </h3>
            <p className="stats-subtitle">Numbers that tell my story</p>
            </div>
          <div className="stats-grid">
            {stats.map((stat, index) => (
                <div 
                  key={stat.key}
                className={`stat-card-premium ${hoveredStat === index ? 'hovered' : ''}`}
                style={{ '--delay': `${index * 0.1}s` }}
                onMouseEnter={() => setHoveredStat(index)}
                onMouseLeave={() => setHoveredStat(null)}
              >
                <div className="stat-card-glow"></div>
                <div className="stat-icon-wrapper">
                  {stat.isImage ? (
                    <img src={stat.icon} alt={stat.label} className="stat-icon stat-icon-image" />
                  ) : (
                    <span className="stat-icon">{stat.icon}</span>
                  )}
                  <div className="icon-ring"></div>
                </div>
                <div className="stat-number">
                  <span className="number-value">{counters[stat.key].toLocaleString()}</span>
                  <span className="number-suffix">{stat.suffix}</span>
                  </div>
                <div className="stat-label">{stat.label}</div>
                <div className="stat-description">{stat.description}</div>
                <div className="stat-progress">
                  <div className="progress-track">
                    <div 
                      className="progress-fill" 
                      style={{ 
                        width: isVisible ? '100%' : '0%',
                        transitionDelay: `${index * 0.15}s`
                      }}
                    ></div>
                  </div>
                  </div>
                </div>
              ))}
          </div>
        </div>


        {/* Quick Facts Grid */}
        <div className="quick-facts-section">
          <div className="facts-header">
            <h3 className="facts-title">
              <span className="title-icon">⚡</span>
              Quick Facts
            </h3>
          </div>
          <div className="facts-grid">
            {quickFacts.map((fact, index) => (
              <div 
                key={fact.label}
                className="fact-card"
                style={{ '--delay': `${index * 0.05}s` }}
              >
                <div className="fact-icon">{fact.icon}</div>
                <div className="fact-content">
                  <span className="fact-label">{fact.label}</span>
                  <span className="fact-value">{fact.value}</span>
                  <span className="fact-detail">{fact.detail}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Philosophy Quote */}
        <div className="philosophy-section">
          <div className="philosophy-card">
            <div className="quote-decoration">"</div>
            <blockquote className="philosophy-quote">
              Code is how ideas become products.
              <br />
              Built with purpose, shipped with reliability, and refined through real&nbsp;users.
            </blockquote>
            <div className="philosophy-attribution">
              <span className="attribution-line"></span>
              <span className="attribution-text">My Development Philosophy</span>
              <span className="attribution-line"></span>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default About;
